import { Device } from '@/api';

export type FeatureState = {
    /** The effective enabled state (pending command target, else reported config). */
    enabled: boolean;
    /** The device's reported enabled state, ignoring any pending command. */
    reportedEnabled: boolean;
    /** Whether a command for this feature is currently in flight. */
    pending: boolean;
};

export type DeviceFeatureStates = {
    fall: FeatureState;
    tilt: FeatureState;
    geofence: FeatureState;
    wifi: FeatureState & { ssids: string[] };
};

/**
 * Derives the device's feature states from its reported config, folding in any
 * pending commands. A pending command reflects the user's intended state, so it
 * overrides the reported config for that feature (and marks it as pending).
 */
export function deriveDeviceFeatureStates(device: Device): DeviceFeatureStates {
    const config = device.config;
    const commands = device.commands ?? [];

    const pendingOfType = (type: string) =>
        commands.filter((c) => c.type === type && c.status === 'pending');

    // Fall
    const fallPending = pendingOfType('set_fall_alert');
    const fallReported = config?.fallAlert?.enabled ?? false;
    const fall: FeatureState = {
        enabled: fallPending[0]
            ? (fallPending[0].data.fallAlertEnabled ?? false)
            : fallReported,
        reportedEnabled: fallReported,
        pending: fallPending.length > 0
    };

    // Tilt
    const tiltPending = pendingOfType('set_tilt_alert');
    const tiltReported = config?.tiltAlert?.enabled ?? false;
    const tilt: FeatureState = {
        enabled: tiltPending[0]
            ? (tiltPending[0].data.tiltAlertEnabled ?? false)
            : tiltReported,
        reportedEnabled: tiltReported,
        pending: tiltPending.length > 0
    };

    // Geofence (per-slot, keyed by slot index)
    const geoSlots = new Map<number, boolean>();
    Object.entries(config?.geoAlert ?? {}).forEach(([index, slot]) => {
        geoSlots.set(Number(index), slot.enabled);
    });
    const geoReported = [...geoSlots.values()].some(Boolean);
    const geoPending = pendingOfType('set_geo_alert');
    geoPending.forEach((c) => {
        if (c.data.geoAlertIndex !== undefined) {
            geoSlots.set(c.data.geoAlertIndex, c.data.geoAlertEnabled ?? false);
        }
    });
    const geofence: FeatureState = {
        enabled: [...geoSlots.values()].some(Boolean),
        reportedEnabled: geoReported,
        pending: geoPending.length > 0
    };

    // Wifi (per-slot, keyed by slot index) — also collect the enabled SSIDs
    const wifiSlots = new Map<
        number,
        { enabled: boolean; ssid: string | null }
    >();
    Object.entries(config?.wifi ?? {}).forEach(([index, slot]) => {
        wifiSlots.set(Number(index), {
            enabled: slot.enabled,
            ssid: slot.ssid
        });
    });
    const wifiReported = [...wifiSlots.values()].some((slot) => slot.enabled);
    const wifiPending = pendingOfType('set_wifi');
    wifiPending.forEach((c) => {
        if (c.data.wifiIndex !== undefined) {
            wifiSlots.set(c.data.wifiIndex, {
                enabled: c.data.wifiEnabled ?? false,
                ssid: c.data.wifiSsid ?? null
            });
        }
    });
    const enabledWifiSlots = [...wifiSlots.values()].filter(
        (slot) => slot.enabled
    );
    const wifi: FeatureState & { ssids: string[] } = {
        enabled: enabledWifiSlots.length > 0,
        reportedEnabled: wifiReported,
        pending: wifiPending.length > 0,
        ssids: enabledWifiSlots
            .map((slot) => slot.ssid)
            .filter((ssid): ssid is string => !!ssid)
    };

    return { fall, tilt, geofence, wifi };
}
